//Global imports
import { useEffect, useState } from 'react';
import { CCol, CFormCheck, CRow } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilFolder } from '@coreui/icons';

//File imports
import { DocumentService } from './Api/DocumentService';
import Notification from '../../services/Notification/Notification';
import { Language } from '../../language/English';
import Logger from '../../services/Logger/Logger';
import DraftResultsPojo from './Api/DocumentResultPojo';
import { DocumentPojo } from './Api/DocumentPojo';

//Component imports
import { Layout } from '../../components/Layout';
import Loader from '../../components/Layout/Loader';
import { DocumentCard } from './Components';
import {
    PAGINATION_DEFAULT_LIMIT,
    PAGINATION_DEFAULT_PAGE,
} from '../../Constant';

const Documents = () => {
    const documentService = new DocumentService();
    const notification = new Notification();
    const logger = new Logger();

    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [documentList, setDocumentList] = useState<
        DraftResultsPojo[][] | null
    >(null);
    const [page, setPage] = useState<number>(PAGINATION_DEFAULT_PAGE);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [showDevDrafts, setShowDevDrafts] = useState<boolean>(false);

    useEffect(() => {
        getAllSuccessDocuments(page);
    }, [page, showDevDrafts]);

    const getAllSuccessDocuments = async (page: number) => {
        setIsLoadingMore(true);
        try {
            const response = await documentService.getAllWithPagination(
                page,
                PAGINATION_DEFAULT_LIMIT,
                showDevDrafts,
            );

            if (response && response.isSuccess) {
                const chunkedDocuments: any = [];
                const rawDocuments = response?.documents?.data || [];

                for (let i = 0; i < rawDocuments.length; i += 2) {
                    chunkedDocuments.push(rawDocuments.slice(i, i + 2));
                }

                if (page === PAGINATION_DEFAULT_PAGE) {
                    setDocumentList(chunkedDocuments);
                } else {
                    setDocumentList((prev) => [
                        ...(prev ?? []),
                        ...chunkedDocuments,
                    ]);
                }

                const totalFetched =
                    (page - 1) * PAGINATION_DEFAULT_LIMIT + rawDocuments.length;

                if (totalFetched >= response?.documents?.totalRecord) {
                    setHasMore(false);
                }
            } else {
                notification.error(Language.CANNOT_LOAD_DOCUMENT_DRAFTS_ERROR);
            }
            setShowLoader(false);
            setIsLoadingMore(false);
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            setShowLoader(false);
            setIsLoadingMore(false);
            logger.error(e);
        }
    };

    const handleLoadMore = () => {
        setPage((prev) => prev + 1);
    };

    const handleShowDevToggle = (checked: boolean) => {
        setShowDevDrafts(checked);
        setDocumentList(null);
        setHasMore(true);
        setPage(PAGINATION_DEFAULT_PAGE);
        setShowLoader(true);
    };

    return (
        <Layout>
            {showLoader ? (
                <Loader />
            ) : (
                <>
                    <CRow className="mb-2 align-items-center">
                        <CCol xs="auto">
                            <CFormCheck
                                id="showDevDocs"
                                reverse
                                checked={showDevDrafts}
                                label={Language.SHOW_DEV_DOCUMENTS}
                                className="fw-semibold border-black-checkbox"
                                onChange={(e) =>
                                    handleShowDevToggle(e.target.checked)
                                }
                            />
                        </CCol>
                    </CRow>
                    {documentList && documentList.length > 0 ? (
                        <>
                            <CRow>
                                {documentList.map((row: any) =>
                                    row.map((documentData: DocumentPojo) => (
                                        <DocumentCard
                                            key={documentData.documentId}
                                            document={documentData}
                                        />
                                    )),
                                )}
                            </CRow>

                            {hasMore && (
                                <div className="text-center mb-4">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleLoadMore}
                                        disabled={isLoadingMore}
                                    >
                                        {isLoadingMore
                                            ? Language.LOADING_
                                            : Language.LOADMORE}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : documentList &&
                      Array.isArray(documentList) &&
                      documentList.length === 0 ? (
                        <div
                            className="d-flex flex-column align-items-center justify-content-center"
                            style={{ minHeight: '60vh' }}
                        >
                            <CIcon
                                icon={cilFolder}
                                size="3xl"
                                className="mb-3 text-secondary"
                            />
                            <h4 className="mb-3 text-center text-secondary fs-6">
                                {Language.NO_DOCUMENTS_CREATED} <br />
                            </h4>
                        </div>
                    ) : null}
                </>
            )}
        </Layout>
    );
};

export default Documents;
